/**
 * ChartSelector Component
 * Displays user's saved natal charts for selection
 * Supports swipe-to-delete gesture
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../../api';
import { useTelegramApp } from '../../hooks';
import type { SavedChartSummary } from '../../types';
import styles from './ChartSelector.module.css';

interface ChartSelectorProps {
  onSelectChart: (chartId: string) => void;
  onOpenSettings?: () => void;
  onOpenFamily?: () => void;
}

export function ChartSelector({ onSelectChart, onOpenSettings, onOpenFamily }: ChartSelectorProps) {
  const { haptic, showConfirmPopup } = useTelegramApp();
  const [charts, setCharts] = useState<SavedChartSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [swipedCardId, setSwipedCardId] = useState<string | null>(null);
  const [hoveredCardId, setHoveredCardId] = useState<string | null>(null);

  // Touch tracking refs
  const touchStartX = useRef<number>(0);
  const touchCurrentX = useRef<number>(0);
  const isDragging = useRef<boolean>(false);

  useEffect(() => {
    const fetchCharts = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await api.getUserCharts();
        if (response.success) {
          setCharts(response.charts);
        } else {
          setError(response.error || 'Failed to load charts');
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Connection error');
      } finally {
        setLoading(false);
      }
    };

    fetchCharts();
  }, []);

  // Close swiped card when clicking outside
  useEffect(() => {
    if (!swipedCardId) return;

    const handleClickOutside = (e: MouseEvent | TouchEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest(`.${styles.chartCardWrapper}`)) {
        setSwipedCardId(null);
      }
    };

    document.addEventListener('click', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);

    return () => {
      document.removeEventListener('click', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [swipedCardId]);

  const handleChartClick = useCallback((chartId: string) => {
    // Don't select if card is swiped
    if (swipedCardId === chartId) {
      setSwipedCardId(null);
      return;
    }
    haptic.selection();
    onSelectChart(chartId);
  }, [haptic, onSelectChart, swipedCardId]);

  const handleOpenSettings = useCallback(() => {
    haptic.impact('light');
    onOpenSettings?.();
  }, [haptic, onOpenSettings]);

  // Swipe handlers
  const handleTouchStart = useCallback((e: React.TouchEvent, chartId: string) => {
    // Close any other swiped card
    if (swipedCardId && swipedCardId !== chartId) {
      setSwipedCardId(null);
    }
    touchStartX.current = e.touches[0].clientX;
    touchCurrentX.current = e.touches[0].clientX;
    isDragging.current = false;
  }, [swipedCardId]);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    touchCurrentX.current = e.touches[0].clientX;
    const diff = touchStartX.current - touchCurrentX.current;

    // Start tracking after 10px movement
    if (Math.abs(diff) > 10) {
      isDragging.current = true;
    }
  }, []);

  const handleTouchEnd = useCallback((chartId: string) => {
    const diff = touchStartX.current - touchCurrentX.current;
    const threshold = 50;

    if (isDragging.current) {
      if (diff > threshold) {
        // Swiped left - show delete button
        haptic.impact('light');
        setSwipedCardId(chartId);
      } else if (diff < -threshold && swipedCardId === chartId) {
        // Swiped right - hide delete button
        setSwipedCardId(null);
      }
    }

    isDragging.current = false;
  }, [haptic, swipedCardId]);

  // Delete handler
  const handleDeleteChart = useCallback(async (chartId: string, chartName: string) => {
    haptic.impact('medium');

    const confirmed = await showConfirmPopup({
      title: 'Удалить карту?',
      message: `"${chartName}" будет удалена без возможности восстановления`,
      confirmText: 'Удалить',
    });

    if (confirmed) {
      const result = await api.deleteChart(chartId);
      if (result.success) {
        haptic.notification('success');
        setCharts(charts.filter(c => c.id !== chartId));
      } else {
        haptic.notification('error');
      }
    }
    setSwipedCardId(null);
  }, [haptic, showConfirmPopup, charts]);

  const formatDate = (dateStr: string) => {
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('ru-RU', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      });
    } catch {
      return dateStr;
    }
  };

  const formatLocation = (locationName: string) => {
    return locationName.split(',')[0].trim();
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingContainer}>
          <div className={styles.spinner} />
          <p>Загрузка карт...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.errorContainer}>
          <p className={styles.errorText}>{error}</p>
          <button className={styles.retryButton} onClick={() => window.location.reload()}>
            Повторить
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1 className={styles.title}>Натальные карты</h1>
        <div className={styles.headerActions}>
          {onOpenFamily && (
            <button
              className={styles.headerBtn}
              onClick={() => { haptic.impact('light'); onOpenFamily(); }}
              aria-label="Семья"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
              </svg>
            </button>
          )}
          {onOpenSettings && (
            <button
              className={styles.headerBtn}
              onClick={handleOpenSettings}
              aria-label="Настройки"
            >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="3" />
              <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
            </svg>
          </button>
        )}
        </div>
      </div>
      <p className={styles.subtitle}>
        {charts.length === 0
          ? 'Создайте свою первую натальную карту'
          : 'Выберите карту или создайте новую'}
      </p>

      {charts.length === 0 ? (
        <div className={styles.emptyState}>
          <p className={styles.emptyText}>Пока нет сохранённых карт</p>
          <p className={styles.emptyHint}>
            Нажмите кнопку ниже, чтобы создать первую карту
          </p>
        </div>
      ) : (
        <div className={styles.chartList}>
          {charts.map((chart) => (
            <div
              key={chart.id}
              className={styles.chartCardWrapper}
              onMouseEnter={() => setHoveredCardId(chart.id)}
              onMouseLeave={() => setHoveredCardId(null)}
            >
              <button
                className={`${styles.chartCard} ${swipedCardId === chart.id ? styles.swiped : ''}`}
                onClick={() => handleChartClick(chart.id)}
                onTouchStart={(e) => handleTouchStart(e, chart.id)}
                onTouchMove={handleTouchMove}
                onTouchEnd={() => handleTouchEnd(chart.id)}
              >
                {/* Delete icon for desktop (hover) */}
                {hoveredCardId === chart.id && !swipedCardId && (
                  <span
                    className={styles.deleteIcon}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteChart(chart.id, chart.name || 'Натальная карта');
                    }}
                    role="button"
                    aria-label="Удалить карту"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2M10 11v6M14 11v6" />
                    </svg>
                  </span>
                )}
                {chart.isPrimary && <span className={styles.primaryBadge}>Основная</span>}

                <div className={styles.chartHeader}>
                  <h3 className={styles.chartName}>
                    {chart.name || 'Натальная карта'}
                  </h3>
                  <div className={styles.signs}>
                    <span className={styles.sunSign} title="Знак Солнца">
                      ☉ {chart.sunSignRu}
                    </span>
                    <span className={styles.moonSign} title="Знак Луны">
                      ☽ {chart.moonSignRu}
                    </span>
                  </div>
                </div>

                <div className={styles.chartDetails}>
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>Дата</span>
                    <span className={styles.detailValue}>{formatDate(chart.birthDate)}</span>
                  </div>
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>Время</span>
                    <span className={styles.detailValue}>{chart.birthTime}</span>
                  </div>
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>Место</span>
                    <span className={styles.detailValue}>{formatLocation(chart.locationName)}</span>
                  </div>
                </div>
              </button>

              <button
                className={styles.deleteButton}
                onClick={() => handleDeleteChart(chart.id, chart.name || 'Натальная карта')}
              >
                Удалить
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default ChartSelector;
